import * as React from 'react';
import * as R from 'ramda';
import RaterApprovalLayout from './RaterApprovalLayout';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { css } from '@emotion/core';
import LocalDiv from '../localization/LocalDiv';
import LocalSelect from '../localization/LocalSelect';
import { FLEX_ROW, FLEX_COLUMN, FLEX_CENTERED, FLEX_ADAPTIVE } from '../layout/flexBox';
import { SELECT } from '../forms/styles/select';
import { useAppState } from '../core/redux/hooks';
import { useHistory } from 'react-router-dom';
import { makeRoutes, useRoutes } from '../navigation/routes';
import { useDispatch } from 'react-redux';
import { RaterApprovalParticipant, RaterApprovalRater } from './models';
import { raterApprovalRatersAction } from './api/raterApprovalRatersAction';
import RaterApprovalTable from './RaterApprovalTable';
import { useLoading, useLoadingOfType } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { Spinner } from '../loading/Spinner';
import { COLORS } from '../globalStyle/colors';
import { loadProjectSummaryAction } from '../project/loadProjectSummaryAction';
import Spacer from '../layout/Spacer';
import { participantByUniqueIdentifier, ratersByApprovalStatuses } from './module';
import { firstLastLocal, lastCommaFirstLocal } from '../project/survey/raters/module';
import { raterLevelsAction } from '../project/survey/raters/raterLevelsAction';
import MobileRaterApprovalToolbar from './MobileRaterApprovalToolbar';
import { useToggle } from '../core/hooks/useToggle';
import { SendTo } from '../project/survey/raters/models';
import { sendRaterApprovalAction } from './api/sendRaterApprovalAction';
import { raterApprovalEmailPreviewAction } from './api/raterApprovalEmailPreviewAction';
import CustomizeEmailModal from '../project/survey/raters/CustomizeEmailModal';
import { RaterApprovalStatus } from './raterApprovalStatus';
import { useMap } from '../core/hooks/useMap';
import { useRaterApprovalIds } from '../navigation/hooks';
import { isNilOrEmpty } from '../language/module';
import { useTimedToggleOnSuccess } from '../core/hooks/useTimedToggleOnSuccess';
import { useCounterOnAnySuccess } from '../core/hooks/useCounterOnAnySuccess';
import { useCounterOnAnyError } from '../core/hooks/useCounterOnAnyError';
import { useToggleOnError } from '../core/hooks/useToggleOnError';
import { addRaterByEmailAsApproverAction } from './api/addRaterByEmailAsApproverAction';
import { approveRaterAction } from './api/approveRaterAction';
import { declineRaterAction } from './api/declineRaterAction';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { approveAllRatersAction } from './api/approveAllRatersAction';
import { useMsgText } from '../localization/hooks';
import { useCallback } from 'react';
import LocalBtn from '../localization/LocalBtn';
import { btnStyle } from '../forms/styles/button';
import ConfirmationModal from '../layout/modal/ConfirmationModal';


const styles = {
  header:             css`
    margin: 20px auto 40px;
    font-size: 26px;
    
    ${onMobileDevice} {
      padding-top: 25px;
    }
  `,
  infoRow:            [
    FLEX_ADAPTIVE,
    css`
      justify-content: space-between;
      margin-bottom: 12px;
    `,
  ],
  selectMsgContainer: {
    root:                  FLEX_COLUMN,
    caption:               FLEX_ROW,
    singleParticipantName: css`font-weight: bold; padding-top: 10px;`,
    select:                [SELECT, css`border-color: ${COLORS.WHITE}`],
    selectContainer:       FLEX_ROW,
  },
  deadline:           {
    root:  [FLEX_ROW, css`align-items: center`],
    label: css`font-weight: bold;`,
  },
  table:              css``,
  button:               css`
    width: max-content;
    padding-left: 12px;
    padding-right: 12px;
  `,
  approveAllBtn:   btnStyle.secondary(COLORS.DARK_GREY),
  padTop10: css`padding-top: 10px;`,
  padTop0: css`padding-top: 0px;`,
};


type Props = {
  className?: string;
}


const mapParticipantsToOptions = (
  participants: RaterApprovalParticipant[],
) => participants.map(
  p => (
    <option
      key={p.projectParticipant}
      value={p.projectParticipant}
      label={p.displayName}
    >
      {p.displayName}
    </option>
  ),
);


/**
 * RaterApproval: a component that enables rater approvers to approve and decline raters for projects, and to invite
 * approved raters to complete the respective survey(s).
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalDashboard: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const history  = useHistory();

  const routes = useRoutes();

  // Local State
  const [addErrorMsg, setAddErrorMsg]   = React.useState<string | undefined>(undefined);
  const [, setEmailAddress] = React.useState(''); //TODO - passed back from add raters form but never used?
  const [emailComment, setEmailComment] = React.useState<string>('');

  // Normally we would just check whether the number of returned update-status requests matches the number of edited
  // raters to determine whether to send the approval email, but since the approver can auto-approved raters and
  // potentially never even have to edit a rater, we have to explicitly indicate when we can send.
  const [readyToSend, toggleSendSignal] = useToggle();

  const [customizeModalVisible, toggleCustomizeModal] = useToggle();

  const [addedRaterCount, resetAddedRaterCount] = useCounterOnAnySuccess(ActionType.AddRaterByEmailAsApproverRequest);
  const [approvalCount, resetApprovalCount]     = useCounterOnAnySuccess(ActionType.ApproveRaterRequest);
  const [declineCount, resetDeclineCount]       = useCounterOnAnySuccess(ActionType.DeclineRaterRequest);

  const { resetDecisionCount } = React.useMemo(
    () => ({
      resetDecisionCount: () => {
        resetApprovalCount();
        resetDeclineCount();
      },
    }),
    [resetApprovalCount, resetDeclineCount],
  );

  const [approvalErrorCount, resetApprovalErrorCount] = useCounterOnAnyError(ActionType.ApproveRaterRequest);
  const [declineErrorCount, resetDeclineErrorCount]   = useCounterOnAnyError(ActionType.DeclineRaterRequest);

  const { updateErrorCount, resetUpdateErrorCount } = React.useMemo(
    () => ({
      updateErrorCount:      approvalErrorCount + declineErrorCount,
      resetUpdateErrorCount: () => {
        resetApprovalErrorCount();
        resetDeclineErrorCount();
      },
    }),
    [approvalErrorCount, declineErrorCount, resetApprovalErrorCount, resetDeclineErrorCount],
  );

  const [ratersToApprove, markRaterToApprove, cancelApprovingRater] = useMap<RaterApprovalRater>({});
  const [ratersToDecline, markRaterToDecline, cancelDecliningRater] = useMap<RaterApprovalRater>({});

  // Props from state
  const { projDisplayId, participantId } = useRaterApprovalIds();

  const approverId    = useAppState(state => state.auth.currentUser?.personId ?? -1);
  const textDirection = useAppState(state => state.locale.textDirection);
  const langInfo      = useAppState(state => state.locale.lang);
  const project       = useAppState(state => state.projectSummary);

  const raterLevels = useAppState(state => state.projectRaterLevels?.[`${project?.projectId}`] ?? []);

  const participants = useAppState(state => state.raterApprovalParticipants ?? []);

  const emailBody = useAppState(
    state => state.raterApprovalEmailTemplates[`${(projectId)}`]?.[`${langInfo.languageId}`] || '',
  );

  const participant = React.useMemo(
    () => participantByUniqueIdentifier(`${(projDisplayId)}|${(participantId)}`, participants),
    [participants, participantId, projDisplayId],
  );

  const raters = useAppState(
    state => state.raterApprovalRaters?.[`${participant?.participantId}`]?.[`${participant?.projectId}`],
  );

  // Convenience props
  const { hasParticipants, hasOneParticipant, hasMultipleParticipants } = React.useMemo(
    () => ({
      hasParticipants:         !!participants?.length,
      hasOneParticipant:       participants?.length === 1,
      hasMultipleParticipants: participants?.length > 1,
    }),
    [participants],
  );

  const selectMsg = React.useMemo(
    () => hasParticipants ? Str.ApproveFor : Str.NoApprovalParticipants,
    [hasParticipants],
  );

  const projectId = React.useMemo(() => participant?.projectId ?? -1, [participant]);

  const participantName = React.useMemo(
    () => participant ? firstLastLocal(textDirection, participant) : undefined,
    [participant, textDirection],
  );

  const pendingRaters = React.useMemo(
    () => ratersByApprovalStatuses(
      [RaterApprovalStatus.None, RaterApprovalStatus.NeedApproval, RaterApprovalStatus.WaitingForApproval],
      raters,
    ),
    [raters],
  );

  const editableRaterMarked = React.useMemo(
    () => !!pendingRaters?.length
          && (!!(Object.keys(ratersToApprove)).length || !!(Object.keys(ratersToDecline)).length),
    [pendingRaters, ratersToApprove, ratersToDecline],
  );

  const participantEmailInfo = React.useMemo<SendTo>(
    () => ({
      toAddress:  participant?.email || '',
      languageId: langInfo.languageId,
    }),
    [langInfo.languageId, participant],
  );

  // Props-dependent functions
  const loadingRaters = useLoading(
    ActionType.RaterApprovalRatersRequest,
    `${participant?.participantId}-${participant?.projectId}`,
  );

  const approvalSent = useTimedToggleOnSuccess(
    ActionType.SendRaterApprovalRequest,
    `${approverId}-${participantId}-${projectId}`,
  );

  const [errorSendingApproval, resetSendingApprovalError] = useToggleOnError(
    ActionType.SendRaterApprovalRequest,
    `${approverId}-${participantId}-${projectId}`,
  );

  // If the backend is already doing work related to approving raters,
  // then we should disable the send approval functionality until we gain back performance capacity
   const sendLoading = useLoadingOfType(
    ActionType.SendRaterApprovalRequest,
  );
  const approveLoading = useLoadingOfType(
    ActionType.ApproveRaterRequest,
  );
  const declineLoading = useLoadingOfType(
    ActionType.DeclineRaterRequest,
  );

  // UI functions
  const disableSendApprovalBtn = React.useMemo(
    () => !isNilOrEmpty(sendLoading)
      || !isNilOrEmpty(approveLoading)
      || !isNilOrEmpty(declineLoading)
      || (!editableRaterMarked && !addedRaterCount),
    [addedRaterCount, editableRaterMarked, sendLoading, approveLoading, declineLoading],
  );
  
  // don't want to keep selections between participants since keys do not include participant id
  const clearSelections = React.useCallback(
    async () => {
      const declineKeys = Object.keys(ratersToDecline);
      R.forEach(raterId => {
        cancelDecliningRater(`${raterId}`);
      }, declineKeys);

      const approveKeys = Object.keys(ratersToApprove);
      R.forEach(raterId => {
        cancelApprovingRater(`${raterId}`);
      }, approveKeys); 
    },
    [
      ratersToDecline, 
      ratersToApprove, 
      cancelDecliningRater, 
      cancelApprovingRater,
    ],
  );

  const onSelectParticipant = React.useCallback(
    event => {
      const identifiers   = event.target.value.split('|');
      const projectId     = identifiers[0];
      const participantId = +identifiers[1];
      clearSelections();
      history.push(makeRoutes(participantId, projectId).RaterApprovalDashboard);
    },
    [history, clearSelections],
  );

  // Approve/deny flow functions
  const markRaterApproved = React.useCallback(
    (rater: RaterApprovalRater) => {
      cancelDecliningRater(`${rater.raterId}`);
      markRaterToApprove(`${rater.raterId}`, rater);
    },
    [markRaterToApprove, cancelDecliningRater],
  );

  const markRaterDeclined = React.useCallback(
    (rater: RaterApprovalRater) => {
      cancelApprovingRater(`${rater.raterId}`);
      markRaterToDecline(`${rater.raterId}`, rater);
    },
    [markRaterToDecline, cancelApprovingRater],
  );

  const addRater = React.useCallback(
    async (levelId: number, email: string, firstName: string, lastName: string) => {
      await dispatch(addRaterByEmailAsApproverAction(projectId, participantId, levelId, email, firstName, lastName));
    },
    [dispatch, participantId, projectId],
  );

  const approveRater = React.useCallback(
    (raterId: number) => dispatch(approveRaterAction(
      participantId,
      projectId,
      raterId,
    )),
    [dispatch, participantId, projectId],
  );

  const declineRater = React.useCallback(
    (raterId: number) => dispatch(declineRaterAction(
      participantId,
      projectId,
      raterId,
    )),
    [dispatch, participantId, projectId],
  );

  const sendApprovalEmail = React.useCallback(
    () => dispatch(
      sendRaterApprovalAction(
        approverId,
        participantId,
        projectId,
        emailComment,
        [participantEmailInfo],
        approvalCount,
        declineCount,
        addedRaterCount,
      ),
    ),
    [
      addedRaterCount,
      approvalCount,
      approverId,
      declineCount,
      dispatch,
      emailComment,
      participantEmailInfo,
      participantId,
      projectId,
    ],
  );

  const approveAllRaters = React.useCallback(
    async () => {
      await clearSelections();

      await dispatch(approveAllRatersAction(participantId, projectId, approverId));

      //loop through all other participants to get updated rater list for each
      for (const participant of participants) {
          if (participant.participantId !== participantId || participant.participantId !== projectId) {
            dispatch(raterApprovalRatersAction(participant.participantId, participant.projectId));
          }
      }
    },
    [
      dispatch, 
      participantId, 
      projectId, 
      approverId, 
      participants,
      clearSelections,
    ],
  ); 

  const sendAllUpdates = React.useCallback(
    async () => {
      if (errorSendingApproval) { resetSendingApprovalError(); }
      resetUpdateErrorCount();
      resetDecisionCount();

      const approveKeys = Object.keys(ratersToApprove);
      const declineKeys = Object.keys(ratersToDecline);

      if (approveKeys.length || declineKeys.length) {
        await Promise.all([
          ...approveKeys.map(raterId => approveRater(+raterId)),
          ...declineKeys.map(raterId => declineRater(+raterId)),
        ]);

        dispatch(sendRaterApprovalAction(
          approverId,
          participantId,
          projectId,
          emailComment,
          [participantEmailInfo],
          approveKeys.length,
          declineKeys.length,
          addedRaterCount,
        ));
        clearSelections();
        resetAddedRaterCount();
      } else {
        toggleSendSignal();
      }
    },
    [
      approveRater,
      declineRater,
      errorSendingApproval,
      ratersToApprove,
      ratersToDecline,
      resetDecisionCount,
      resetSendingApprovalError,
      resetUpdateErrorCount,
      toggleSendSignal,
      dispatch,
      approverId,
      participantId,
      projectId,
      emailComment,
      participantEmailInfo,
      addedRaterCount,
      clearSelections,
      resetAddedRaterCount,
    ],
  );

  // Effects
  React.useEffect(
    () => {
      if (projectId > 0 && !!langInfo && !emailBody) {
        dispatch(raterApprovalEmailPreviewAction(projectId, langInfo.languageId));
      }
    },
    [dispatch, emailBody, langInfo, projectId],
  );

  React.useEffect(
    () => {
      if (!!participant && !raters) {
        dispatch(raterApprovalRatersAction(participant.participantId, participant.projectId));
      }
    },
    [dispatch, participant, raters],
  );

  React.useEffect(
    () => {
      if (!!participant && project?.projectId !== participant.projectId) {
        dispatch(loadProjectSummaryAction(participant.projectId, participant.participantId));
      }
    },
    [dispatch, participant, project],
  );

  React.useEffect(
    () => {
      if (!!project && isNilOrEmpty(raterLevels)) {
        dispatch(raterLevelsAction(projectId));
      }
    },
    [ dispatch, project, projectId, raterLevels],
  );

  React.useEffect(
    () => {
      if (readyToSend) {
        toggleSendSignal();
        resetDecisionCount();
        sendApprovalEmail();
        resetAddedRaterCount();
        clearSelections();
      }
    },
    [
      readyToSend,
      resetAddedRaterCount,
      resetDecisionCount,
      sendApprovalEmail,
      toggleSendSignal,
      clearSelections,
    ],
  );

  const [showConfirmation, toggleShowConfirmation] = useToggle(false);

  const confirmationMsg = useMsgText(Str.ConfirmAllRaters);

  const onConfirm = useCallback(
    () => {
      approveAllRaters();
      toggleShowConfirmation();
    },
    [toggleShowConfirmation, approveAllRaters],
  );   

  return (
    <RaterApprovalLayout>

      <LocalDiv css={styles.header}>
        <Msg str={Str.RaterApproval} />
      </LocalDiv>

      <LocalDiv css={styles.infoRow}>
        <LocalDiv css={styles.selectMsgContainer.root}>
          <LocalDiv css={styles.selectMsgContainer.caption}>
            <Msg str={selectMsg} css={hasOneParticipant ? styles.padTop10 : styles.padTop0}/>
            {
              hasOneParticipant && (
                <>
                  <Spacer sizePx={4} />
                  <span css={styles.selectMsgContainer.singleParticipantName}>
                    {lastCommaFirstLocal(textDirection, participants[0])}
                  </span>
                  <Spacer sizePx={8} />
                  <LocalBtn
                    str={Str.ApproveAllRaters}
                    css={[styles.button, styles.approveAllBtn]}
                    onClick={toggleShowConfirmation}
                  /> 
                </>
              )
            }
          </LocalDiv>
          {
            hasMultipleParticipants && (
              <>
                <Spacer sizePx={8} />
                <LocalDiv css={styles.selectMsgContainer.selectContainer}>
                  <LocalSelect
                    css={styles.selectMsgContainer.select}
                    onChange={onSelectParticipant}
                    value={participant?.projectParticipant}
                    defaultValue={participant?.projectParticipant}
                  >
                    {mapParticipantsToOptions(participants)}
                  </LocalSelect>
                  <Spacer sizePx={8} />
                  <LocalBtn
                    str={Str.ApproveAllRaters}
                    css={[styles.button, styles.approveAllBtn]}
                    onClick={toggleShowConfirmation}
                  /> 
                </LocalDiv>
              </>
            )
          }
        </LocalDiv>
        {
          !!project && (
            <>
              <Spacer sizePx={8} />
              <LocalDiv css={styles.deadline.root}>
                <Msg str={Str.SurveyDeadlineColon} css={styles.deadline.label} />
                <Spacer sizePx={8} />
                <span>{new Date(project.surveyDeadline).toLocaleString(langInfo.code)}</span>
              </LocalDiv>
            </>
          )
        }
      </LocalDiv>

      {
        !!raters && (
          <RaterApprovalTable
            css={styles.table}
            raters={raters}
            raterLevels={raterLevels}
            ratersToApprove={ratersToApprove}
            ratersToDecline={ratersToDecline}
            textDirection={textDirection}
            participantName={participantName || ''}
            showNameFields={!!project?.inviteRatersPromptRaterNames}
            requireNamesOnAdd={!!project?.inviteRatersRequireRaterNames}
            addErrorMsg={addErrorMsg}
            ratersNotUpdatedCount={updateErrorCount}
            showProcessingErrorMsg={errorSendingApproval}
            showApprovalSentMsg={approvalSent}
            disableCustomizeModalButton={false}
            disableSendApprovalButton={disableSendApprovalBtn}
            toggleCustomizeEmailModal={toggleCustomizeModal}
            setEmailAddress={setEmailAddress}
            setAddErrorMsg={setAddErrorMsg}
            addRater={addRater}
            markRaterApproved={markRaterApproved}
            markRaterDeclined={markRaterDeclined}
            sendApprovalEmail={sendAllUpdates}
          />
        )
      }

      {
        loadingRaters && (
          <div css={[FLEX_COLUMN, FLEX_CENTERED, css`height: calc(100vh - 220px);`]}>
            <Spinner fill={COLORS.GREEN} css={css`width: 60px;`} />
          </div>
        )
      }

      {
        customizeModalVisible && (
          <CustomizeEmailModal
            dismiss={toggleCustomizeModal}
            modalTitle={Str.CustomizeEmail}
            emailBody={emailBody}
            emailComment={emailComment}
            setEmailComment={setEmailComment}
            ieIframeRoute={routes.RaterApprovalEmailPreview}
            emailLogo={''}
            participant={participantName ?? ''}
            survey={''}
            dateDue={''}
            ratersApproved={`${(Object.keys(ratersToApprove)).length}`}
            ratersDeclined={`${(Object.keys(ratersToDecline)).length}`}
            ratersAdded={`${addedRaterCount}`}          
          />
        )
      }

      <MobileRaterApprovalToolbar
        disableCustomizeModalButton={false}
        disableSendApprovalButton={disableSendApprovalBtn}
        onClickCustomizeModalButton={toggleCustomizeModal}
        onClickSendMsgButton={sendAllUpdates}
      />
      { // Confirmation Modal
        showConfirmation && (
          <ConfirmationModal
            confirmationTxt={confirmationMsg}
            primaryActionStr={Str.No}
            secondaryActionStr={Str.Yes}
            onSelectPrimary={toggleShowConfirmation}
            onSelectSecondary={onConfirm}
            onDismiss={toggleShowConfirmation}
          />
        )
      }
    </RaterApprovalLayout>
  );
};

export default RaterApprovalDashboard;
